// Feedback from inside Ahillah (the owner, 24 October 2026, DECISIONS 112): what someone tells us is not right, or an
// idea, with what it was about and the details that help look into it (src/live/feedback.ts). The Worker checks it
// here and keeps it in a D1 database for a year; the dev server checks it the same way (vite.config.ts).

/** The most a message may weigh, details and all. */
export const FEEDBACK_MAX = 16_384;
/** The most a person may write. */
export const FEEDBACK_TEXT_MAX = 1500;
/** The details that go with it (the phone, Ahillah's settings, the sky shown), at most. */
export const FEEDBACK_CONTEXT_MAX = 8192;
/** How long a message is kept: the Worker's hourly task deletes older ones. */
export const FEEDBACK_KEEP_DAYS = 365;
/** Which is closest: it looks wrong, it did not work, it is hard to use, an idea. */
export const FEEDBACK_KINDS = ['wrong', 'broken', 'hard', 'idea'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/** A message as it is kept. `uid`: the phone gives each message its own, so one sent twice (an answer lost on the way, or
 *  two open copies of Ahillah sending what waits) is kept once. */
export interface FeedbackRow { uid: string; aboutKey: string; aboutLabel: string; kind: FeedbackKind | null; text: string; context: string }
export const isFeedbackId = (s: unknown): s is string => typeof s === 'string' && /^[A-Za-z0-9_-]{16,64}$/.test(s);

/** Control characters other than new lines become spaces. */
const clean = (s: string) => s.replace(/[\u0000-\u0009\u000B-\u001F\u007F]/g, ' ').trim();

/** A message from the app, checked: the row to keep, or why it is refused. It needs what it is about, and which is
 *  closest or a few words (or both). */
export function parseFeedback(body: string): FeedbackRow | string {
  let v: unknown;
  try {
    v = JSON.parse(body);
  } catch {
    return 'bad json';
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return 'bad message';
  const m = v as Record<string, unknown>;
  if (m.v !== 1) return 'bad version';
  if (!isFeedbackId(m.id)) return 'bad id';
  const about = m.about as Record<string, unknown> | null | undefined;
  if (!about || typeof about !== 'object' || typeof about.key !== 'string' || typeof about.label !== 'string') return 'bad about';
  const aboutKey = clean(about.key);
  const aboutLabel = clean(about.label);
  if (!/^[\x20-\x7E]{1,120}$/.test(aboutKey) || !aboutLabel || aboutLabel.length > 120) return 'bad about';
  const kind = m.kind ?? null;
  if (kind !== null && !FEEDBACK_KINDS.includes(kind as FeedbackKind)) return 'bad kind';
  if (m.text !== undefined && typeof m.text !== 'string') return 'bad text';
  const text = typeof m.text === 'string' ? clean(m.text) : '';
  if (text.length > FEEDBACK_TEXT_MAX) return 'too long';
  if (kind === null && !text) return 'empty';
  const c = m.context;
  if (c !== undefined && (!c || typeof c !== 'object' || Array.isArray(c))) return 'bad context';
  const context = JSON.stringify(c ?? {});
  if (context.length > FEEDBACK_CONTEXT_MAX) return 'too large';
  return { uid: m.id as string, aboutKey, aboutLabel, kind: kind as FeedbackKind | null, text, context };
}
